// frontend/src/components/sales/NewSale.js
//
// Two catalogs feed the same cart now: Finished Goods (fixed catalog
// price, exactly as before) and Raw Inventory (editable price per
// line — raw materials routinely get sold at a negotiated rate rather
// than one fixed price). See models/index.js#SaleItemSchema.sourceType
// for the full reasoning and how the backend enforces the fixed price
// server-side for finished goods regardless of what a client sends.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as api from '../../api';
import { fmt, fmtK, PayBadge, StatCard, Pager, CollapsibleCard, printSaleReceipt } from '../common/CommonComponents';
import CustomerPicker from '../customers/CustomerPicker';

function NewSale({ showAlert, user, onCartChange }) {
  const [products, setProducts] = useState([]);
  const [rawItems, setRawItems] = useState([]);
  const [catalogSource, setCatalogSource] = useState("finishedProduct"); // "finishedProduct" | "rawInventory"
  const [cartItems, setCartItems] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [rawPrice, setRawPrice] = useState(""); // editable price when adding a raw-inventory line
  const [discountType, setDiscountType] = useState("none");
  const [discountValue, setDiscountValue] = useState("");
  const [discountReason, setDiscountReason] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [paymentRef, setPaymentRef] = useState("");
  const [ambassadorEmail, setAmbassadorEmail] = useState("");
  const [ambassadors, setAmbassadors] = useState([]);
  const [customerId, setCustomerId] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const saleOperationId=useRef(null),salePayloadSignature=useRef(null),saleSubmitLock=useRef(false);
  const makeOperationId=()=>window.crypto?.randomUUID?.()||`sale-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const [receipt, setReceipt] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [viewMode, setViewMode] = useState("grid"); // "grid" or "list"
  const [ambassadorSearch, setAmbassadorSearch] = useState("");
  const [ambassadorDropOpen, setAmbassadorDropOpen] = useState(false);

  const hasRawMaterials = user?.planFeatures?.hasRawMaterials !== false;
  const hasCRM = user?.planFeatures?.hasCRM === true;
  const canCreateCustomers = ["manager", "sales"].includes(user.role);

  useEffect(() => {
      loadProducts();
      loadRawItems();
      api.getAmbassadors().then(setAmbassadors).catch(() => {});
    }, []);

    useEffect(() => {
      if (onCartChange) onCartChange(cartItems.reduce((s, i) => s + i.quantity, 0));
    }, [cartItems, onCartChange]);

  const loadProducts = () => {
    api.getFinishedGoods({ activeOnly: "true", locationOnly: "true" }).then(setProducts).catch(() => {});
  };
  const loadRawItems = () => {
    if (!hasRawMaterials) return;
    api.getRawInventoryItems({ locationOnly: "true" }).then(r => setRawItems(r.items || [])).catch(() => {});
  };

  // Both catalogs normalized to the same shape for the picker/cart:
  // productId, name, sellingPrice, quantity (available), category.
  const catalogList = catalogSource === "finishedProduct"
    ? products.map(p => ({ productId: p.productId, name: p.name, imageUrl: p.imageUrl, sellingPrice: p.sellingPrice, costPrice: p.costPrice, quantity: p.quantity, category: p.category, sourceType: "finishedProduct" }))
    : rawItems.map(i => ({ productId: i.itemId, name: i.name, imageUrl: null, sellingPrice: i.sellingPrice, costPrice: i.costPerUnit, quantity: i.quantityLeft, category: i.category, unit: i.unit, sourceType: "rawInventory" }));

  const categories = ["all", ...new Set(catalogList.map(p => p.category).filter(Boolean))];

  const filteredProducts = catalogList.filter(p => {
    if (p.quantity <= 0) return false;
    if (selectedCategory !== "all" && p.category !== selectedCategory) return false;
    if (searchTerm && !p.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !(p.category || "").toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const switchSource = (src) => {
    setCatalogSource(src);
    setSelectedProduct(null);
    setSearchTerm("");
    setSelectedCategory("all");
  };

  const addToCart = (product, priceOverride) => {
    if (product.quantity <= 0) {
      showAlert("Out of stock", "error");
      return;
    }
    if (qty > product.quantity) {
      showAlert(`Only ${product.quantity} ${product.unit || ""} in stock`, "error");
      return;
    }
    const price = product.sourceType === "rawInventory"
      ? Number(priceOverride !== undefined ? priceOverride : rawPrice)
      : product.sellingPrice;
    if (product.sourceType === "rawInventory" && !(price >= 0)) {
      showAlert("Enter a valid price for this item", "error");
      return;
    }

    setCartItems(c => {
      // Raw inventory lines with a DIFFERENT price than an existing cart
      // line for the same item are kept separate (someone selling half
      // at one price and half at another isn't a bug to merge away) —
      // only merge when sourceType, id, AND price all match.
      const existing = c.find(i => i.productId === product.productId && i.sourceType === product.sourceType && i.sellingPrice === price);
      if (existing) {
        const newQty = existing.quantity + Number(qty);
        if (newQty > product.quantity) {
          showAlert(`Only ${product.quantity} ${product.unit || ""} in stock total`, "error");
          return c;
        }
        return c.map(i => i === existing ? { ...i, quantity: newQty, subtotal: i.sellingPrice * newQty } : i);
      }
      return [...c, {
        productId: product.productId,
        productName: product.name,
        productImage: product.imageUrl,
        costPrice: product.costPrice,
        sellingPrice: price,
        quantity: Number(qty),
        subtotal: price * Number(qty),
        sourceType: product.sourceType,
        unit: product.unit || "",
        availableQty: product.quantity,
      }];
    });
    setSelectedProduct(null);
    setQty(1);
    setRawPrice("");
  };

  const removeFromCart = (idx) => {
    setCartItems(c => c.filter((_, i) => i !== idx));
  };

  const updateCartQuantity = (idx, newQty) => {
    if (newQty < 1) { removeFromCart(idx); return; }
    setCartItems(c => c.map((item, i) => {
      if (i !== idx) return item;
      if (newQty > item.availableQty) {
        showAlert(`Only ${item.availableQty} ${item.unit || ""} in stock`, "error");
        return item;
      }
      return { ...item, quantity: newQty, subtotal: item.sellingPrice * newQty };
    }));
  };

  const updateCartPrice = (idx, newPrice) => {
    const price = Number(newPrice);
    if (!(price >= 0)) return;
    setCartItems(c => c.map((item, i) => i === idx ? { ...item, sellingPrice: price, subtotal: price * item.quantity } : item));
  };

  const grossTotal = cartItems.reduce((s, i) => s + i.subtotal, 0);
  const discountAmount = (() => {
    if (discountType === "percentage") return Math.round((grossTotal * (Number(discountValue || 0) / 100)) * 100) / 100;
    if (discountType === "fixed") return Math.min(Number(discountValue || 0), grossTotal);
    return 0;
  })();
  const totalAmount = grossTotal - discountAmount;

  const submitSale = async () => {
    if (cartItems.length === 0) {
      showAlert("Add at least one item", "error");
      return;
    }
    if (discountType !== "none" && Number(discountValue) > 0 && !discountReason) {
      showAlert("Enter a reason for the discount (required for audit)", "error");
      return;
    }
    if(saleSubmitLock.current)return;
    saleSubmitLock.current=true;setSubmitting(true);
    try {
      const payload={
        items: cartItems.map(i => ({ productId: i.productId, quantity: i.quantity, sourceType: i.sourceType, sellingPrice: i.sellingPrice })),
        discountType, discountValue: Number(discountValue || 0), discountReason,
        paymentMethod, paymentReference: paymentRef || null, ambassadorEmail: ambassadorEmail || null,
        customerId: customerId || null,
      };
      const signature=JSON.stringify(payload);
      if(!saleOperationId.current||salePayloadSignature.current!==signature){saleOperationId.current=makeOperationId();salePayloadSignature.current=signature;}
      const sale = await api.createSale(payload,saleOperationId.current);
      saleOperationId.current=null;salePayloadSignature.current=null;
      setReceipt(sale);
      setCartItems([]);
      setDiscountType("none");
      setDiscountValue("");
      setDiscountReason("");
      setPaymentMethod("Cash");
      setPaymentRef("");
      setAmbassadorEmail("");
      setCustomerId(null);
      setCustomerName("");
      showAlert(`Sale submitted! ID: ${sale.saleId} — Awaiting payment confirmation. Invoice ready to print.`);
      loadProducts();
      loadRawItems();
    } catch (e) {
      showAlert(e.message, "error");
    } finally { saleSubmitLock.current=false;setSubmitting(false); }
  };

  if (receipt) {
    return (
      <div className="card" style={{ textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🧾</div>
        <h2 style={{ color: "#10B981" }}>Sale Submitted!</h2>
        <p style={{ color: "var(--text-3)", marginBottom: 16 }}>Awaiting payment confirmation — print the invoice now, or any time from Pending Sales before payment is confirmed.</p>
        <div className="preview-box" style={{ textAlign: "left", marginBottom: 16 }}>
          <div><strong>Sale ID:</strong> {receipt.saleId}</div>
          <div className="divider" />
          {receipt.items.map((i, idx) => (
            <div key={idx} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0" }}>
              <span>{i.productName} × {i.quantity}</span>
              <span>{fmt(i.subtotal)}</span>
            </div>
          ))}
          {receipt.discountAmount > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", color: "#E94560", padding: "4px 0" }}>
              <span>Discount</span>
              <span>-{fmt(receipt.discountAmount)}</span>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: 16, marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--border)" }}>
            <span>Total</span>
            <span style={{ color: "#10B981" }}>{fmt(receipt.totalAmount)}</span>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
          <button className="btn btn-primary" onClick={() => { setReceipt(null); setCustomerId(null); setCustomerName(""); }}>New Sale</button>
          <button className="btn btn-secondary" onClick={() => printSaleReceipt(receipt, user.businessName, user.businessLogo, user.planFeatures?.hasCustomBranding, "invoice")}>🖨 Print Invoice</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="section-header">
        <div>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800 }}>🛒 Point of Sale</h2>
          <p style={{ fontSize: 12, color: "var(--text-3)", marginTop: 4 }}>Select products from catalog to build customer invoice</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className={`btn-sm ${viewMode === "grid" ? "btn-primary" : "btn-secondary"}`} onClick={() => setViewMode("grid")}>
            📱 Grid
          </button>
          <button className={`btn-sm ${viewMode === "list" ? "btn-primary" : "btn-secondary"}`} onClick={() => setViewMode("list")}>
            📋 List
          </button>
        </div>
      </div>

      {hasRawMaterials && (
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <button onClick={() => switchSource("finishedProduct")}
            className={`btn-sm ${catalogSource === "finishedProduct" ? "btn-primary" : "btn-secondary"}`}>
            🛍️ Finished Goods
          </button>
          <button onClick={() => switchSource("rawInventory")}
            className={`btn-sm ${catalogSource === "rawInventory" ? "btn-primary" : "btn-secondary"}`}>
            🧱 Raw Inventory
          </button>
        </div>
      )}

      <div className="newsale-layout">
        {/* Product Catalog Section */}
        <div className="card" style={{ padding: 16 }}>
          <div style={{ marginBottom: 16 }}>
            <div className="search-bar" style={{ marginBottom: 12 }}>
              <input
                placeholder={`🔍 Search ${catalogSource === "finishedProduct" ? "products" : "raw inventory"}...`}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            {categories.length > 1 && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`btn-sm ${selectedCategory === cat ? "btn-primary" : "btn-secondary"}`}
                    style={{ fontSize: 11 }}
                  >
                    {cat === "all" ? "All" : cat}
                  </button>
                ))}
              </div>
            )}
            {catalogSource === "rawInventory" && (
              <div className="alert alert-info" style={{ fontSize: 11, padding: "6px 10px" }}>
                💡 Raw inventory sells at an editable price — set it per sale below.
              </div>
            )}
          </div>

          {viewMode === "grid" ? (
            <div className="product-catalog-grid">
              {filteredProducts.map(p => (
                <div
                  key={p.productId}
                  className="product-card"
                  onClick={() => { setSelectedProduct(p); setQty(1); setRawPrice(p.sellingPrice || ""); }}
                  style={{
                    border: `1px solid ${selectedProduct?.productId === p.productId ? "var(--accent)" : "var(--border)"}`,
                    borderRadius: 12, overflow: "hidden", cursor: "pointer", background: "var(--surface)",
                    transition: "all 0.2s"
                  }}
                >
                  <div style={{ height: 120, background: "var(--surface2)", overflow: "hidden" }}>
                    {p.imageUrl ? (
                      <img src={p.imageUrl} alt={p.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", fontSize: 40 }}>{p.sourceType === "rawInventory" ? "🧱" : "🛍️"}</div>
                    )}
                  </div>
                  <div style={{ padding: 10 }}>
                    <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: "var(--accent)", marginBottom: 4 }}>
                      {p.sourceType === "rawInventory" ? `from ${fmt(p.sellingPrice)}` : fmt(p.sellingPrice)}
                    </div>
                    <div style={{ fontSize: 10, color: p.quantity <= 5 ? "#EF4444" : "var(--text-3)", marginBottom: 8 }}>
                      Stock: {p.quantity} {p.unit || ""}
                    </div>
                    <button
                      className="btn btn-primary"
                      style={{ width: "100%", fontSize: 11, padding: "6px 8px" }}
                      onClick={e => { e.stopPropagation(); setSelectedProduct(p); setQty(1); setRawPrice(p.sellingPrice || ""); if (p.sourceType === "finishedProduct") addToCart(p); }}
                      disabled={p.quantity === 0}
                    >
                      {p.sourceType === "rawInventory" ? "Set price →" : (cartItems.find(i => i.productId === p.productId) ? "➕ Add More" : "🛒 Add to Cart")}
                    </button>
                  </div>
                </div>
              ))}
              {filteredProducts.length === 0 && (
                <div style={{ textAlign: "center", padding: 48, color: "var(--text-3)", gridColumn: "1/-1" }}>
                  <div style={{ fontSize: 40, marginBottom: 8 }}>{catalogSource === "rawInventory" ? "🧱" : "🛍️"}</div>
                  <div>No {catalogSource === "rawInventory" ? "raw inventory" : "products"} found</div>
                </div>
              )}
            </div>
          ) : (
            <div className="product-list-view">
              {filteredProducts.map(p => (
                <div
                  key={p.productId}
                  onClick={() => { setSelectedProduct(p); setQty(1); setRawPrice(p.sellingPrice || ""); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 12, padding: 10,
                    borderBottom: "1px solid var(--border)", cursor: "pointer",
                    background: selectedProduct?.productId === p.productId ? "var(--surface2)" : "transparent"
                  }}
                >
                  <div style={{ width: 40, height: 40, borderRadius: 6, background: "var(--surface2)", overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {p.imageUrl ? <img src={p.imageUrl} alt={p.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (p.sourceType === "rawInventory" ? "🧱" : "🛍️")}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: "var(--text-3)" }}>{p.category}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontWeight: 700, color: "var(--accent)" }}>{p.sourceType === "rawInventory" ? `from ${fmt(p.sellingPrice)}` : fmt(p.sellingPrice)}</div>
                    <div style={{ fontSize: 10, color: "var(--text-3)" }}>Stock: {p.quantity} {p.unit || ""}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Quick Add Quantity (+ price for raw inventory) */}
          {selectedProduct && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--border)" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 140 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>Selected: {selectedProduct.name}</div>
                  <div style={{ fontSize: 11, color: "var(--text-3)" }}>
                    Available: {selectedProduct.quantity} {selectedProduct.unit || ""}
                    {selectedProduct.sourceType === "rawInventory" && ` · Cost: ${fmt(selectedProduct.costPrice)}/${selectedProduct.unit || "unit"}`}
                  </div>
                </div>
                {selectedProduct.sourceType === "rawInventory" && (
                  <div className="form-group" style={{ margin: 0, width: 110 }}>
                    <label style={{ fontSize: 10 }}>Price (₦) *</label>
                    <input type="number" min={0} value={rawPrice} onChange={e => setRawPrice(e.target.value)} style={{ textAlign: "center" }} />
                  </div>
                )}
                <div className="form-group" style={{ margin: 0, width: 80 }}>
                  <label style={{ fontSize: 10 }}>Qty</label>
                  <input
                    type="number"
                    min={1}
                    max={selectedProduct.quantity}
                    value={qty}
                    onChange={e => setQty(Math.min(selectedProduct.quantity, Math.max(1, Number(e.target.value))))}
                    style={{ textAlign: "center" }}
                  />
                </div>
                <button className="btn btn-primary" onClick={() => addToCart(selectedProduct)}>
                  + Add to Cart
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Shopping Cart Section */}
        <div className="card newsale-cart" id="cart-section">
          <h2 style={{ marginBottom: 12 }}>🛒 Cart ({cartItems.length} item{cartItems.length !== 1 ? "s" : ""})</h2>
          
          {cartItems.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: "var(--text-3)" }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>🛒</div>
              <p>Cart is empty</p>
              <p style={{ fontSize: 11 }}>Tap a product above to add</p>
            </div>
          ) : (
            <>
              <div className="cart-items-list">
                {cartItems.map((item, idx) => (
                  <div key={idx} style={{ display: "flex", gap: 10, padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                    <div style={{ width: 40, height: 40, borderRadius: 6, background: "var(--surface2)", overflow: "hidden", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {item.productImage ? (
                        <img src={item.productImage} alt={item.productName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (item.sourceType === "rawInventory" ? "🧱" : "🛍️")}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 12 }}>{item.productName}</div>
                      {item.sourceType === "rawInventory" ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <span style={{ fontSize: 10, color: "var(--text-3)" }}>₦</span>
                          <input type="number" min={0} value={item.sellingPrice}
                            onChange={e => updateCartPrice(idx, e.target.value)}
                            style={{ width: 64, fontSize: 11, padding: "2px 4px" }} />
                          <span style={{ fontSize: 10, color: "var(--text-3)" }}>/{item.unit || "unit"}</span>
                        </div>
                      ) : (
                        <div style={{ fontSize: 11, color: "var(--text-3)" }}>{fmt(item.sellingPrice)} each</div>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <button className="btn-sm btn-secondary" onClick={() => updateCartQuantity(idx, item.quantity - 1)}>−</button>
                      <span style={{ minWidth: 30, textAlign: "center", fontWeight: 600 }}>{item.quantity}</span>
                      <button className="btn-sm btn-secondary" onClick={() => updateCartQuantity(idx, item.quantity + 1)}>+</button>
                      <button className="btn-sm btn-reject" onClick={() => removeFromCart(idx)}>✕</button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="preview-box" style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span>Subtotal</span>
                  <span>{fmt(grossTotal)}</span>
                </div>
                {discountAmount > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: "#E94560" }}>
                    <span>Discount</span>
                    <span>-{fmt(discountAmount)}</span>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, fontSize: 16, marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--border)" }}>
                  <span>Total</span>
                  <span style={{ color: "#10B981" }}>{fmt(totalAmount)}</span>
                </div>
              </div>

              {/* Discount Section */}
              <details style={{ marginBottom: 16 }}>
                <summary style={{ cursor: "pointer", fontSize: 12, fontWeight: 600, color: "var(--accent)" }}>Add Discount</summary>
                <div style={{ marginTop: 12 }}>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Type</label>
                      <select value={discountType} onChange={e => { setDiscountType(e.target.value); setDiscountValue(""); }}>
                        <option value="none">No Discount</option>
                        <option value="percentage">Percentage (%)</option>
                        <option value="fixed">Fixed Amount (₦)</option>
                      </select>
                    </div>
                    {discountType !== "none" && (
                      <div className="form-group">
                        <label>{discountType === "percentage" ? "Percentage (%)" : "Amount (₦)"}</label>
                        <input type="number" value={discountValue} onChange={e => setDiscountValue(e.target.value)} min={0} />
                      </div>
                    )}
                  </div>
                  {discountType !== "none" && Number(discountValue) > 0 && (
                    <div className="form-group">
                      <label>Reason *</label>
                      <input value={discountReason} onChange={e => setDiscountReason(e.target.value)} placeholder="e.g., Loyal customer, promo" />
                    </div>
                  )}
                </div>
              </details>

              {/* Customer (Tier 13, Growth+ only) */}
              {hasCRM && (
                <div style={{ marginBottom: 16 }}>
                  <CustomerPicker
                    value={customerId}
                    valueName={customerName}
                    canCreate={canCreateCustomers}
                    showAlert={showAlert}
                    creationContext="this sale"
                    onSelect={c => { setCustomerId(c.customerId); setCustomerName(c.name); }}
                    onClear={() => { setCustomerId(null); setCustomerName(""); }}
                  />
                </div>
              )}

              {/* Ambassador */}
              <div className="form-group" style={{ marginBottom: 16, position: "relative" }}>
                <label>Tag Ambassador (if referred)</label>
                <input
                  type="text"
                  placeholder="Search ambassador by name or email..."
                  value={ambassadorSearch}
                  onChange={e => { setAmbassadorSearch(e.target.value); setAmbassadorEmail(""); }}
                  onFocus={() => setAmbassadorDropOpen(true)}
                  onBlur={() => setTimeout(() => setAmbassadorDropOpen(false), 200)}
                  style={{ width: "100%", padding: "9px 12px", border: "1.5px solid var(--border)", borderRadius: 8, fontSize: 13, outline: "none", background: "var(--input-bg)", color: "var(--text)" }}
                />
                {ambassadorEmail && (
                  <div style={{ fontSize: 11, color: "#10b981", marginTop: 4 }}>
                    ✓ Tagged: {ambassadors.find(a => a.email === ambassadorEmail)?.name || ambassadorEmail}
                    <button onClick={() => { setAmbassadorEmail(""); setAmbassadorSearch(""); }} style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer", marginLeft: 6 }}>✕</button>
                  </div>
                )}
                {ambassadorDropOpen && ambassadorSearch && (
                  <div style={{ position: "absolute", left: 0, right: 0, top: "calc(100% + 2px)", background: "var(--surface)", border: "1.5px solid var(--accent)", borderRadius: 8, boxShadow: "0 6px 20px rgba(0,0,0,.12)", zIndex: 300, maxHeight: 200, overflowY: "auto" }}>
                    {ambassadors
                      .filter(a => (a.name || "").toLowerCase().includes(ambassadorSearch.toLowerCase()) || a.email.toLowerCase().includes(ambassadorSearch.toLowerCase()))
                      .map(a => (
                        <button key={a.email} onMouseDown={() => { setAmbassadorEmail(a.email); setAmbassadorSearch(a.name || a.email); setAmbassadorDropOpen(false); }}
                          style={{ display: "block", width: "100%", padding: "10px 14px", background: "none", border: "none", borderBottom: "1px solid var(--border)", cursor: "pointer", textAlign: "left", fontSize: 13 }}
                          onMouseEnter={e => e.currentTarget.style.background = "var(--surface2)"}
                          onMouseLeave={e => e.currentTarget.style.background = "none"}
                        >
                          <div style={{ fontWeight: 600 }}>{a.name || a.email}</div>
                          <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{a.email}</div>
                        </button>
                      ))
                    }
                    {ambassadors.filter(a => (a.name || "").toLowerCase().includes(ambassadorSearch.toLowerCase()) || a.email.toLowerCase().includes(ambassadorSearch.toLowerCase())).length === 0 && (
                      <div style={{ padding: "12px 14px", color: "var(--text-muted)", fontSize: 13 }}>No ambassadors found</div>
                    )}
                  </div>
                )}
              </div>

              {/* Payment Method */}
              <div className="form-row" style={{ marginBottom: 16 }}>
                <div className="form-group">
                  <label>Payment Method *</label>
                  <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
                    <option value="Cash">💵 Cash</option>
                    <option value="Transfer">🏦 Bank Transfer</option>
                    <option value="POS">💳 POS / Card</option>
                  </select>
                </div>
                {paymentMethod === "Transfer" && (
                  <div className="form-group">
                    <label>Reference Number</label>
                    <input value={paymentRef} onChange={e => setPaymentRef(e.target.value)} placeholder="Transfer reference" />
                  </div>
                )}
              </div>

              {/* Payment Instructions */}
              {paymentMethod === "Cash" && (
                <div className="alert alert-warning" style={{ marginBottom: 16 }}>
                  💵 Collect cash from customer before confirming sale.
                </div>
              )}
              {paymentMethod === "Transfer" && (
                <div className="alert alert-info" style={{ marginBottom: 16 }}>
                  🏦 Ask customer to send payment. Verify before confirming.
                </div>
              )}

              <button className="btn btn-primary" style={{ width: "100%" }} onClick={submitSale} disabled={submitting}>
                {submitting ? "Processing..." : `💳 Complete Sale — ${fmt(totalAmount)}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default NewSale;
